import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Cancel,
  TrendingUp,
  ShowChart,
  TrendingDown,
  HelpOutline,
} from '@mui/icons-material';
import { ReadinessStatus, ConfidenceLevel } from '../../types/readinessConfidence.types';

interface MatrixCell {
  readiness: ReadinessStatus;
  confidence: ConfidenceLevel;
  interpretation: string;
  trustLevel: 'high' | 'medium' | 'low' | 'insufficient';
  bgColor: string;
  borderColor: string;
}

const MATRIX_DATA: MatrixCell[] = [
  // PASS row
  {
    readiness: 'PASS',
    confidence: 'HIGH',
    interpretation: 'High Trust',
    trustLevel: 'high',
    bgColor: '#c8e6c9',
    borderColor: '#4caf50',
  },
  {
    readiness: 'PASS',
    confidence: 'MEDIUM',
    interpretation: 'Moderate Trust',
    trustLevel: 'medium',
    bgColor: '#fff9c4',
    borderColor: '#fbc02d',
  },
  {
    readiness: 'PASS',
    confidence: 'LOW',
    interpretation: 'Low Trust',
    trustLevel: 'low',
    bgColor: '#ffe0b2',
    borderColor: '#ff9800',
  },
  {
    readiness: 'PASS',
    confidence: 'INSUFFICIENT',
    interpretation: 'Insufficient Data',
    trustLevel: 'insufficient',
    bgColor: '#f5f5f5',
    borderColor: '#9e9e9e',
  },
  // WARN row
  {
    readiness: 'WARN',
    confidence: 'HIGH',
    interpretation: 'Mixed Trust',
    trustLevel: 'medium',
    bgColor: '#fff9c4',
    borderColor: '#fbc02d',
  },
  {
    readiness: 'WARN',
    confidence: 'MEDIUM',
    interpretation: 'Caution',
    trustLevel: 'low',
    bgColor: '#ffe0b2',
    borderColor: '#ff9800',
  },
  {
    readiness: 'WARN',
    confidence: 'LOW',
    interpretation: 'Low Trust',
    trustLevel: 'low',
    bgColor: '#ffccbc',
    borderColor: '#ff7043',
  },
  {
    readiness: 'WARN',
    confidence: 'INSUFFICIENT',
    interpretation: 'Insufficient Data',
    trustLevel: 'insufficient',
    bgColor: '#f5f5f5',
    borderColor: '#9e9e9e',
  },
  // FAIL row
  {
    readiness: 'FAIL',
    confidence: 'HIGH',
    interpretation: 'Do Not Trust',
    trustLevel: 'low',
    bgColor: '#ffcdd2',
    borderColor: '#f44336',
  },
  {
    readiness: 'FAIL',
    confidence: 'MEDIUM',
    interpretation: 'Do Not Trust',
    trustLevel: 'low',
    bgColor: '#ffcdd2',
    borderColor: '#f44336',
  },
  {
    readiness: 'FAIL',
    confidence: 'LOW',
    interpretation: 'Do Not Trust',
    trustLevel: 'low',
    bgColor: '#ffcdd2',
    borderColor: '#f44336',
  },
  {
    readiness: 'FAIL',
    confidence: 'INSUFFICIENT',
    interpretation: 'Do Not Trust',
    trustLevel: 'low',
    bgColor: '#ffcdd2',
    borderColor: '#f44336',
  },
];

interface ReadinessMatrixProps {
  currentReadiness?: ReadinessStatus;
  currentConfidence?: ConfidenceLevel;
  onCellClick?: (readiness: ReadinessStatus, confidence: ConfidenceLevel) => void;
}

const ReadinessMatrix: React.FC<ReadinessMatrixProps> = ({
  currentReadiness,
  currentConfidence,
  onCellClick,
}) => {
  const readinessList: ReadinessStatus[] = ['PASS', 'WARN', 'FAIL'];
  const confidenceList: ConfidenceLevel[] = ['HIGH', 'MEDIUM', 'LOW', 'INSUFFICIENT'];

  const getCell = (readiness: ReadinessStatus, confidence: ConfidenceLevel): MatrixCell => {
    return MATRIX_DATA.find((c) => c.readiness === readiness && c.confidence === confidence)!;
  };

  const getConfidenceIcon = (confidence: ConfidenceLevel) => {
    switch (confidence) {
      case 'HIGH':
        return <TrendingUp />;
      case 'MEDIUM':
        return <ShowChart />;
      case 'LOW':
        return <TrendingDown />;
      case 'INSUFFICIENT':
        return <HelpOutline />;
    }
  };

  const getReadinessIcon = (readiness: ReadinessStatus) => {
    switch (readiness) {
      case 'PASS':
        return <CheckCircle />;
      case 'WARN':
        return <Warning />;
      case 'FAIL':
        return <Cancel />;
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Readiness × Confidence Matrix
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 600 }}>
            {/* Header row */}
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item xs={2.4}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Readiness ↓ / Confidence →
                </Typography>
              </Grid>
              {confidenceList.map((conf) => (
                <Grid item xs={1.9} key={conf}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getConfidenceIcon(conf) as any}
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {conf}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Matrix rows */}
            {readinessList.map((readiness) => (
              <Grid container spacing={1} key={readiness} sx={{ mb: 1 }}>
                {/* Row label */}
                <Grid item xs={2.4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 1 }}>
                    {getReadinessIcon(readiness) as any}
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {readiness}
                    </Typography>
                  </Box>
                </Grid>

                {/* Cells */}
                {confidenceList.map((confidence) => {
                  const cell = getCell(readiness, confidence);
                  const isCurrent =
                    currentReadiness === readiness && currentConfidence === confidence;

                  return (
                    <Grid item xs={1.9} key={`${readiness}-${confidence}`}>
                      <Tooltip title={cell.interpretation} arrow>
                        <Paper
                          onClick={() => onCellClick?.(readiness, confidence)}
                          sx={{
                            p: 1.5,
                            textAlign: 'center',
                            cursor: onCellClick ? 'pointer' : 'default',
                            bgcolor: cell.bgColor,
                            border: isCurrent ? `3px solid ${cell.borderColor}` : `2px solid ${cell.borderColor}`,
                            transition: 'all 0.2s ease',
                            '&:hover': onCellClick
                              ? {
                                  transform: 'scale(1.05)',
                                  boxShadow: 3,
                                }
                              : {},
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {cell.interpretation}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
            ))}
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
            Trust Levels:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{ width: 16, height: 16, bgcolor: '#c8e6c9', border: '2px solid #4caf50' }}
                />
                <Typography variant="caption">High Trust</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{ width: 16, height: 16, bgcolor: '#fff9c4', border: '2px solid #fbc02d' }}
                />
                <Typography variant="caption">Medium Trust</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{ width: 16, height: 16, bgcolor: '#ffccbc', border: '2px solid #ff7043' }}
                />
                <Typography variant="caption">Low Trust</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{ width: 16, height: 16, bgcolor: '#f5f5f5', border: '2px solid #9e9e9e' }}
                />
                <Typography variant="caption">Insufficient</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReadinessMatrix;
